import React, { type FC } from 'react';
import { CardShell } from './card-shell';
import { CARD_PADDING_X, CARD_PADDING_Y } from './card-ui';

/* ---------- Primitives ---------- */

// `shimmerSlide` lives in global.css. It used to be injected as an inline
// <style> by every mounted ShimmerCard, which also made it the accidental home
// of the `pulse` keyframe used by a component elsewhere in the tree, so that
// component only animated when a shimmer card happened to be on screen.
const shimmerStyle: React.CSSProperties = {
  background:
    'linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0,0,0,0.06) 50%, rgba(0,0,0,0.04) 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmerSlide 1.5s ease-in-out infinite',
};

export const ShimmerLine: FC<{ width?: string; height?: number }> = ({
  width = '75%',
  height = 12,
}) => (
  <div
    style={{ ...shimmerStyle, width, height, borderRadius: 6 }}
  />
);

export const ShimmerCircle: FC<{ size?: number }> = ({ size = 40 }) => (
  <div
    style={{ ...shimmerStyle, width: size, height: size, borderRadius: '50%', flexShrink: 0 }}
  />
);

export const ShimmerPill: FC<{ width?: number }> = ({ width = 56 }) => (
  <div
    style={{ ...shimmerStyle, width, height: 20, borderRadius: 9999 }}
  />
);

export const ShimmerBlock: FC<{ width?: string | number; height?: number; radius?: number }> = ({
  width = '100%',
  height = 80,
  radius = 12,
}) => (
  <div style={{ ...shimmerStyle, width, height, borderRadius: radius }} />
);

/* ---------- Skeleton layouts ---------- */

const carouselSkeleton = (
  <div style={{ display: 'flex', gap: 12, overflow: 'hidden' }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{ minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <ShimmerBlock height={100} radius={12} />
        <ShimmerLine width="80%" />
        <ShimmerLine width="50%" height={10} />
        <div style={{ display: 'flex', gap: 6 }}>
          <ShimmerPill width={48} />
          <ShimmerPill width={40} />
          <ShimmerPill width={52} />
        </div>
      </div>
    ))}
  </div>
);

const profileSkeleton = (
  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
    <ShimmerCircle size={40} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <ShimmerLine width="60%" />
      <ShimmerLine width="40%" height={10} />
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <ShimmerPill width={48} />
        <ShimmerPill width={40} />
        <ShimmerPill width={52} />
      </div>
    </div>
  </div>
);

const detailSkeleton = (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <ShimmerLine width="30%" height={10} />
    <ShimmerLine width="70%" />
    <ShimmerLine width="90%" height={10} />
    <ShimmerLine width="60%" height={10} />
    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
      <ShimmerPill width={48} />
      <ShimmerPill width={40} />
      <ShimmerPill width={52} />
    </div>
  </div>
);

const compactSkeleton = (
  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
    <ShimmerCircle size={20} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <ShimmerLine width="55%" />
      <ShimmerLine width="35%" height={10} />
    </div>
  </div>
);

const messageSkeleton = (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <ShimmerLine width="100%" />
    <ShimmerLine width="95%" />
    <ShimmerLine width="80%" />
    <ShimmerLine width="60%" />
  </div>
);

/**
 * A stack of rows, matching what `CardRow` produces once the data lands: a
 * leading square, a title line and two subtitle lines, divided.
 */
const listSkeleton = (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minHeight: 56,
          padding: `10px ${CARD_PADDING_X}px`,
          borderTop: i > 0 ? '1px solid var(--input-border)' : 'none',
        }}
      >
        <ShimmerBlock width={34} height={34} radius={10} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <ShimmerLine width="45%" />
          <ShimmerLine width="85%" height={10} />
        </div>
      </div>
    ))}
  </div>
);

const runSkeleton = (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <ShimmerLine width="40%" />
    <ShimmerBlock height={64} radius={12} />
    <ShimmerLine width="55%" height={10} />
  </div>
);

/**
 * Research renders as one activity line (see research-card.tsx), so its
 * skeleton is that line, drawn without a card around it.
 */
const researchLineSkeleton = (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
    <ShimmerCircle size={16} />
    <ShimmerLine width="140px" height={12} />
  </div>
);

/** A document or deck being made: the canvas entry card's shape. */
const artifactSkeleton = (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 56 }}>
    <ShimmerCircle size={20} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <ShimmerLine width="60%" height={16} />
      <ShimmerLine width="35%" height={10} />
    </div>
    <div style={{ width: 84, height: 40, borderRadius: 9999, background: 'var(--tabbio-surface-container-high)' }} />
  </div>
);

/**
 * Every `cardType` the registry can produce needs an entry. A missing one falls
 * back to `compactSkeleton` (a 20px circle and two short lines), so a skills
 * list, a connector list or an automation plan "loaded" as something shaped
 * nothing like the card that replaced it.
 */
const SKELETON_MAP: Record<string, React.ReactNode> = {
  'job-list': carouselSkeleton,
  'candidate-list': carouselSkeleton,
  'profile-summary': profileSkeleton,
  'candidate-detail': profileSkeleton,
  'cv-detail': detailSkeleton,
  'cv-publish': detailSkeleton,
  'cv-export': detailSkeleton,
  'cv-share': detailSkeleton,
  'job-detail': detailSkeleton,
  'application-preview': compactSkeleton,
  'application-confirm': compactSkeleton,
  'application-list': detailSkeleton,
  'message-draft': messageSkeleton,
  'message-confirm': compactSkeleton,
  'profile-update': compactSkeleton,
  'candidate-action': compactSkeleton,
  'company-update': compactSkeleton,
  'company-list': detailSkeleton,
  'asset-attach': compactSkeleton,
  'asset-recall': listSkeleton,
  'cv-section-preview': detailSkeleton,
  // Previously unmapped: these all fell back to `compactSkeleton`.
  'skill-list': listSkeleton,
  'skill-detail': detailSkeleton,
  'skill-run': runSkeleton,
  'connector-list': listSkeleton,
  'connector-events': listSkeleton,
  'connector-action': compactSkeleton,
  'automation-list': listSkeleton,
  'automation-detail': detailSkeleton,
  'automation-run': runSkeleton,
  'automation-run-list': listSkeleton,
  memory: detailSkeleton,
  'memory-action': compactSkeleton,
  'subscription-blocked': compactSkeleton,
  artifact: artifactSkeleton,
  'artifact-list': listSkeleton,
  // The site-publish card is the same flat tile as the canvas card.
  'site-publish': artifactSkeleton,
  // Suggested edits arrive in the same flat tile.
  'artifact-suggestions': artifactSkeleton,
};

/* ---------- ShimmerCard ---------- */

/**
 * Renders through `CardShell` so the skeleton is, by construction, the same box
 * as the card that replaces it. See the note in card-shell.tsx for what
 * happened when the two kept their own copies of the geometry.
 *
 * List-shaped skeletons draw their own row padding, so only the others get the
 * shell's inner padding.
 */
export const ShimmerCard: FC<{ cardType?: string; toolName: string }> = ({ cardType }) => {
  if (cardType === 'research') {
    return <div aria-hidden="true">{researchLineSkeleton}</div>;
  }
  const skeleton = (cardType && SKELETON_MAP[cardType]) || compactSkeleton;
  const selfPadded = skeleton === listSkeleton;

  return (
    <CardShell>
      <div
        aria-hidden="true"
        style={selfPadded ? undefined : { padding: `${CARD_PADDING_Y}px ${CARD_PADDING_X}px` }}
      >
        {skeleton}
      </div>
    </CardShell>
  );
};
